import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState } from 'react'

// Emoji sets for different milestones
const MILESTONE_EMOJI = {
  1: { emoji: ['🎉', '✨', '🎊', '💫'], message: '🎉 First achievement!' },
  10: { emoji: ['💬', '🗨️', '💭', '💬'], message: '💬 10 completed!' },
  25: { emoji: ['🎊', '⭐', '🌟', '✨'], message: '🎊 Quarter milestone!' },
  50: { emoji: ['⭐', '💫', '✨', '🌟'], message: '⭐ Halfway there!' },
  100: { emoji: ['🏆', '🎉', '🎁', '🌟'], message: '🏆 Century achieved!' },
  default: { emoji: ['✨', '⭐', '💫', '🌟'], message: '✨ Milestone reached!' },
  complete: { emoji: ['👏', '🎉', '✨', '💫'], message: '👏 Report complete!' },
}

export default function CelebrationBurst({ show, milestone, onComplete }) {
  const [particles, setParticles] = useState([])

  // Get milestone config
  const config = milestone === 'complete'
    ? MILESTONE_EMOJI.complete
    : milestone === 1 || milestone === 10 || milestone === 25 || milestone === 50 || milestone === 100
    ? MILESTONE_EMOJI[milestone]
    : milestone > 100 && milestone % 50 === 0
    ? MILESTONE_EMOJI[50] // Reuse 50th style for 150, 200, etc
    : MILESTONE_EMOJI.default

  useEffect(() => {
    if (show) {
      // Generate 8-12 particles
      const particleCount = Math.floor(Math.random() * 5) + 8
      const newParticles = Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        emoji: config.emoji[i % config.emoji.length],
        angle: (360 / particleCount) * i + (Math.random() * 30 - 15), // Spread evenly with randomness
        distance: 150 + Math.random() * 100, // How far from center
        duration: 3 + Math.random() * 2, // 3-5 seconds
        delay: Math.random() * 0.3, // Stagger start
      }))

      setParticles(newParticles)

      // Auto-complete after longest animation
      const maxDuration = Math.max(...newParticles.map(p => p.duration + p.delay))
      const timeout = setTimeout(onComplete, maxDuration * 1000)

      return () => clearTimeout(timeout)
    }
  }, [show, onComplete, config.emoji])

  if (!show) return null

  return (
    <div className="celebration-container" style={{
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      pointerEvents: 'none'
    }}>
      <AnimatePresence>
        {/* Milestone Message */}
        <motion.div
          key="message"
          initial={{ opacity: 0, y: -20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{
            position: 'absolute',
            top: '80px',
            textAlign: 'center'
          }}
        >
          <div style={{
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '16px 32px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            backdropFilter: 'blur(12px)',
            border: '2px solid rgba(59, 130, 246, 0.3)'
          }}>
            <div style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: '#1e293b',
              letterSpacing: '0.05em'
            }}>
              {config.message}
            </div>
          </div>
        </motion.div>

        {/* Emoji Particles */}
        {particles.map((particle) => {
          const radians = (particle.angle * Math.PI) / 180
          const endX = Math.cos(radians) * particle.distance
          const endY = Math.sin(radians) * particle.distance - 80 // Bias upward

          return (
            <motion.div
              key={particle.id}
              initial={{
                x: 0,
                y: 0,
                opacity: 0,
                scale: 0,
                rotate: 0,
              }}
              animate={{
                x: endX,
                y: endY,
                opacity: [0, 1, 1, 0],
                scale: [0, 1.2, 1, 0.8],
                rotate: [0, 360 * (Math.random() > 0.5 ? 1 : -1)],
              }}
              transition={{
                duration: particle.duration,
                delay: particle.delay,
                ease: [0.33, 1, 0.68, 1], // Custom easing for float
              }}
              style={{
                position: 'absolute',
                fontSize: '3.75rem',
                filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))',
                textShadow: '0 0 20px rgba(191, 229, 255, 0.5)',
              }}
            >
              {particle.emoji}
            </motion.div>
          )
        })}
      </AnimatePresence>

      {/* Subtle background flash */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.1, 0] }}
        transition={{ duration: 0.8 }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.2), rgba(251, 146, 60, 0.1))'
        }}
      />
    </div>
  )
}